"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { DownloadForm } from "@/.storyblok/types/319751/storyblok-components";
import VideoPlayer from "@/components/VideoPlayer";

interface FormConfig {
  pdfUrl: string;
  mailchimpUrl: string;
  listId: string;
  tags?: string;
  groupId?: string;
  requiredFields: string[];
  successMessage: string;
}

const DownloadFormComponent: React.FC<{ blok: DownloadForm & SbBlokData }> = ({ blok }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [downloadFormData, setDownloadFormData] = useState({
    FNAME: "",
    LNAME: "",
    EMAIL: "",
    PDONAME: "",
    PHONE: "",
    MMERGE6: false,
    marketingConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ success?: boolean; message?: string }>({});
  const [showDownloadLink, setShowDownloadLink] = useState(false);

  const { image, headline, title, subtitle, description, button_label, quote_type, video, mailchimp_id } = blok;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const FORM_CONFIGS: Record<string, FormConfig> = {
    download_gdpr: {
      pdfUrl: "https://a.storyblok.com/f/319751/x/124a3042f7/bulletproof_-_gdpr_white_paper_2020.pdf",
      mailchimpUrl: "https://bulletproof.us16.list-manage.com/subscribe/post-json?u=def13ad022f20422977f543ef&id=102940bf08&f_id=002669e0f0&c=?",
      listId: "b8d5a1b7ec",
      groupId: "7030",
      requiredFields: ["FNAME", "LNAME", "EMAIL"],
      successMessage: "Thank you! Your GDPR whitepaper download has started."
    },
    hire_data_protection: {
      pdfUrl: "https://a.storyblok.com/f/319751/x/3ddc7b58e6/bulletproof_-_complete_guide_to_hiring_a_dpo.pdf",
      mailchimpUrl: "https://bulletproof.us16.list-manage.com/subscribe/post-json?u=def13ad022f20422977f543ef&id=102940bf08&f_id=002669e0f0&c=?",
      listId: "6ea9b9215d",
      groupId: "6537",
      requiredFields: ["FNAME", "LNAME", "EMAIL"],
      successMessage: "Thank you! Your DPO guide download has started."
    },
    video_report: {
      pdfUrl: "https://a.storyblok.com/f/319751/x/124a3042f7/bulletproof_-_gdpr_white_paper_2020.pdf",
      mailchimpUrl: "https://bulletproof.us16.list-manage.com/subscribe/post-json?u=def13ad022f20422977f543ef&id=102940bf08&f_id=002669e0f0&c=?",
      listId: "102940bf08",
      tags: "3545190",
      requiredFields: ["FNAME", "LNAME", "EMAIL", "PDONAME"],
      successMessage: "Thank you! Your video report download has started."
    },
    default: {
      pdfUrl: "https://a.storyblok.com/f/319751/x/124a3042f7/bulletproof_-_gdpr_white_paper_2020.pdf",
      mailchimpUrl: "https://bulletproof.us16.list-manage.com/subscribe/post-json?u=def13ad022f20422977f543ef&id=102940bf08&f_id=002669e0f0&c=?",
      listId: "102940bf08",
      requiredFields: ["FNAME", "LNAME", "EMAIL", "PDONAME"],
      successMessage: "Thank you! Your download has started."
    }
  };

  const getFormConfig = (): FormConfig => {
    if (!quote_type) {
      return FORM_CONFIGS.default;
    }
    return FORM_CONFIGS[quote_type as keyof typeof FORM_CONFIGS] || FORM_CONFIGS.default;
  };

  const handleSubmissionError = (error: any) => {
    console.error('Form submission error:', error);
    setIsSubmitting(false);
    setSubmitStatus({
      success: false,
      message: error instanceof Error ? error.message : "An error occurred while submitting the form. Please try again.",
    });
  };

  const handleDownloadFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setDownloadFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = (config: FormConfig) => {
    const fieldLabels = {
      FNAME: "First Name",
      LNAME: "Last Name",
      EMAIL: "Email",
      PDONAME: "Company Name"
    };

    for (const field of config.requiredFields) {
      if (field === "EMAIL") {
        if (!downloadFormData.EMAIL || !downloadFormData.EMAIL.includes('@')) {
          throw new Error("Please enter a valid email address");
        }
      } else if (!downloadFormData[field as keyof typeof downloadFormData]) {
        throw new Error(`${fieldLabels[field as keyof typeof fieldLabels]} is required`);
      }
    }
  };

  const initiateDownload = (url: string) => {
    window.open(url, "_blank");
  };

  const resetForm = () => {
    setDownloadFormData({
      FNAME: "",
      LNAME: "",
      EMAIL: "",
      PDONAME: "",
      PHONE: "",
      MMERGE6: false,
      marketingConsent: false,
    });
  };

  const getDownloadUrl = (config: FormConfig): string => {
    return blok.file_download?.filename || config.pdfUrl;
  };

  const handleFormSuccess = (config: FormConfig) => {
    setSubmitStatus({
      success: true,
      message: config.successMessage,
    });
    resetForm();
    setTimeout(() => initiateDownload(getDownloadUrl(config)), 1000);
  };

  
  const createMailchimpScript = (config: FormConfig, callbackName: string): string => {
    let baseUrl = "https://bulletproof.us16.list-manage.com/subscribe/post-json";
    let params = new URLSearchParams();

    if (mailchimp_id) {
      try {
        const cleanUrl = mailchimp_id.replace(/&amp;/g, '&');
        const url = new URL(cleanUrl);
        baseUrl = url.origin + url.pathname.replace('/post', '/post-json');
        params = new URLSearchParams(url.search);
      } catch (e) {
        console.error('Invalid Mailchimp URL:', e);
      }
    }
    
    const formParams = {
      FNAME: downloadFormData.FNAME,
      LNAME: downloadFormData.LNAME,
      EMAIL: downloadFormData.EMAIL,
      PDONAME: downloadFormData.PDONAME,
      PHONE: downloadFormData.PHONE,
      MMERGE6: downloadFormData.MMERGE6 ? "Yes" : "No",
      c: callbackName,
    };

    if (config.groupId) {
      params.set(`group[${config.groupId}][1]`, "1");
    }

    if (config.tags) {
      params.set("tags", config.tags);
    }

    Object.entries(formParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });

    return `${baseUrl}?${params.toString()}`;
  };

  const handleDownloadSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isMounted) return;

    setIsSubmitting(true);
    setSubmitStatus({});

    try {
      const config = getFormConfig();
      validateForm(config);

      if (quote_type === "download_gdpr" && !downloadFormData.marketingConsent) {
        setShowDownloadLink(true);
        setIsSubmitting(false);
        initiateDownload(getDownloadUrl(config));
        return;
      }

      const callbackName = "mc_callback_" + Math.floor(Math.random() * 1000000);

      (window as { [key: string]: any })[callbackName] = (response: any) => {
        setIsSubmitting(false);

        if (response.result === "success") {
          handleFormSuccess(config);
        } else {
          setSubmitStatus({
            success: false,
            message: response.msg || "There was an error submitting the form.",
          });
        }

        document.body.removeChild(script);
        delete (window as { [key: string]: any })[callbackName];
      };

      const script = document.createElement("script");
      script.src = createMailchimpScript(config, callbackName);
      document.body.appendChild(script);

    } catch (error) {
      handleSubmissionError(error);
    }
  };

  const renderFormFields = () => {
    const showCompanyFields = quote_type !== "download_gdpr" && quote_type !== "hire_data_protection";

    return (
      <>
        <div className="mb-3">
          <input
            type="text"
            className="form-input"
            name="FNAME"
            id="mce-FNAME"
            placeholder="First name*"
            value={downloadFormData.FNAME}
            onChange={handleDownloadFormChange}
            disabled={isSubmitting}
            required
          />
        </div>
        <div className="mb-3">
          <input
            type="text"
            className="form-input"
            name="LNAME"
            id="mce-LNAME"
            placeholder="Last name*"
            value={downloadFormData.LNAME}
            onChange={handleDownloadFormChange}
            disabled={isSubmitting}
            required
          />
        </div>
        <div className="mb-3">
          <input
            type="email"
            className="form-input"
            name="EMAIL"
            id="mce-EMAIL"
            placeholder="Email address*"
            value={downloadFormData.EMAIL}
            onChange={handleDownloadFormChange}
            disabled={isSubmitting}
            required
          />
        </div>

        {showCompanyFields && (
          <>
            <div className="mb-3">
              <input
                type="text"
                className="form-input"
                name="PDONAME"
                id="mce-PDONAME"
                placeholder="Company name*"
                value={downloadFormData.PDONAME}
                onChange={handleDownloadFormChange}
                disabled={isSubmitting}
                required
              />
            </div>
            <div className="mb-3">
              <input
                type="text"
                className="form-input"
                name="PHONE"
                id="mce-PHONE"
                placeholder="Phone number"
                value={downloadFormData.PHONE}
                onChange={handleDownloadFormChange}
                disabled={isSubmitting}
              />
            </div>
            <div className="form-row marketing-opt-in opt-in-checkbox text-center">
              <label htmlFor="mce-MMERGE6" className="text-center">
                <input
                  type="checkbox"
                  id="mce-MMERGE6"
                  name="MMERGE6"
                  checked={downloadFormData.MMERGE6}
                  onChange={handleDownloadFormChange}
                  disabled={isSubmitting}
                />
                <small className="text-white">
                  I&apos;d like to receive free cyber and compliance resources, and stay up-to-date with Bulletproof
                  services.{" "}
                  <Link href="/privacy-notice" className="text-decoration-underline" target="_blank">
                    Privacy policy
                  </Link>
                </small>
              </label>
            </div>
          </>
        )}

        {quote_type === "download_gdpr" && (
          <div className="form-row marketing-opt-in">
            <label htmlFor="marketing-consent">
              <input
                type="checkbox"
                id="marketing-consent"
                name="marketingConsent"
                checked={downloadFormData.marketingConsent}
                onChange={handleDownloadFormChange}
                disabled={isSubmitting}
              />
              <small>I agree to receive sales and marketing communication.</small>
            </label>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-secondary d-block w-100"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Processing..." : (button_label || "Download now!")}
        </button>
      </>
    );
  };

  const renderGdprForm = () => (
    <div className="container" {...storyblokEditable(blok)}>
      <div className="p-4">
        <div className="two-thirds-centered mb-4">
          <div className="lead-magnet-form-half lead-magnet-gdpr-form">
            <div className="lead-magnet-image">
              {image?.filename && (
                <Image
                  src={image.filename}
                  alt={image.alt || "GDPR White paper"}
                  width={300}
                  height={300}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
              )}
            </div>
            <div className="lead-magnet-content">
              {headline && <h3>{headline}</h3>}
              {description && <p className="mb-2">{description}</p>}

              {submitStatus.message && (
                <div
                  className={`alert ${submitStatus.success ? "alert-success" : "alert-danger"} mb-3`}
                  role="alert"
                >
                  {submitStatus.message}
                </div>
              )}

              {showDownloadLink ? (
                <div className="text-center">
                  <Link
                    href={getDownloadUrl(FORM_CONFIGS.download_gdpr)}
                    className="btn btn-lg btn-primary mt-2 mt-lg-3"
                    target="_blank"
                  >
                    Click to download
                  </Link>
                </div>
              ) : (
                <form
                  id="gdpr-capture-form"
                  onSubmit={handleDownloadSubmit}
                  className="validate form-horizontal"
                  noValidate
                >
                  {renderFormFields()}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDpoHireForm = () => (
    <div className="lead-magnet-form-half lead-magnet-dpo-form rounded-4 mb-4">
      <div className="lead-magnet-content">
        <h3>{headline || "How to Hire a Data Protection Officer"}</h3>
        <p className="mb-2">{description || "A complete guide to your best options"}</p>

        {submitStatus.message && (
          <div
            className={`alert ${submitStatus.success ? "alert-success" : "alert-danger"} mb-3`}
            role="alert"
          >
            <div dangerouslySetInnerHTML={{ __html: submitStatus.message }} />
          </div>
        )}

        <form
          id="dpo-capture-form"
          className="validate form-horizontal"
          onSubmit={handleDownloadSubmit}
          noValidate
        >
          {renderFormFields()}
        </form>
      </div>
    </div>
  );

  const renderVideoReportForm = () => {
    const videoId = video?.[0]?.tag;

    return (
      <div className="container py-4 py-md-5" id="download" {...storyblokEditable(blok)}>
        {headline && <h2 className="text-center mb-4">{headline}</h2>}
        <div className="row pt-4 g-0">
          <div className="col-lg-18 col-xl-16">
            <div className="bg-dark text-white h-100 d-flex align-items-center">
              {videoId && <VideoPlayer videoId={videoId} />}
            </div>
          </div>
          <div className="col-lg-6 col-xl-8">
            <div className="bg-primary text-white p-3 p-xl-4 py-3 h-100 position-relative">
              {title && <h2 className="h4 font-weight-bold text-center text-white mb-3">{title}</h2>}

              {submitStatus.message && (
                <div
                  className={`alert ${submitStatus.success ? "alert-success" : "alert-danger"} mb-3`}
                  role="alert"
                >
                  {submitStatus.message}
                </div>
              )}

              <form onSubmit={handleDownloadSubmit} method="post" className="validate" noValidate>
                {renderFormFields()}
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDefaultForm = () => (
    <div className="container mb-6" {...storyblokEditable(blok)}>
      <div id="download" className="row g-0 rounded-4 overflow-hidden justify-content-center">
        <div className="col-24 col-lg-10 text-center d-flex align-items-center justify-content-center img-blur">
          {image?.filename && (
            <Image
              src={image.filename}
              alt={image.alt || headline || "Download Form"}
              className="scroll-trigger slide-up delay-1s duration-1s img-blur-fg on"
              width={465}
              height={658}
              loading="lazy"
              quality={75}
            />
          )}
        </div>
        <div className="col-24 col-lg-14">
          <div className="bg-primary text-white p-3 p-xl-4 py-3 h-100 position-relative">
            {headline && <h2 className="text-white">{headline}</h2>}
            {title && <h3 className="text-white">{title}</h3>}
            {subtitle && <h4 className="text-white">{subtitle}</h4>}
            {description && <p className="mb-4">{description}</p>}

            {submitStatus.message && (
              <div
                className={`alert ${submitStatus.success ? "alert-success" : "alert-danger"} mb-3`}
                role="alert"
              >
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleDownloadSubmit} method="post" className="validate" noValidate>
              {renderFormFields()}
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  if (quote_type === "download_gdpr") {
    return renderGdprForm();
  }

  if (quote_type === "hire_data_protection") {
    return renderDpoHireForm();
  }

  if (quote_type === "video_report") {
    return renderVideoReportForm();
  }

  return renderDefaultForm();
};

export default DownloadFormComponent;